import { useEffect } from "react";
import { Button, LanguageSwitch, Wordmark, cn } from "@ghezaa/ui";
import { returnUrlWithoutMarker, signInHref, signInMarker, useSession } from "../session";
import { Trans, useLingui } from "@lingui/react/macro";

export type SignInPageProps = {
  /** Small label beside the logo: "Core", "Orders", "Support". */
  appName: string;
  /** The big serif statement on the left. */
  headline: string;
  /** One or two sentences under the headline. */
  description: string;
  /** Message shown when the previous attempt failed (`?signin=failed`). */
  failureMessage?: string;
};

/**
 * Two-pane sign-in: statement on the canvas, one action on the surface. Shared by every Ghezaa app. The button always
 * opens Microsoft's account picker, so switching accounts is sign out, press, choose. Signed in to another Ghezaa app on
 * this device (`deviceSignedIn`, the `ghz.device` hint), the page does not wait for the press: it goes to Microsoft, which
 * knows the account, and straight back. Not after a marked round trip (`?signin=auto` or `failed`: the attempt was made,
 * so the button it is), and only on an answer fetched since the page opened, never a cached one from before a sign-out.
 */
export function SignInPage({
  appName,
  headline,
  description,
  failureMessage,
}: SignInPageProps) {
  const session = useSession();
  const development = session.data?.mode === "Development";
  const marker = signInMarker();
  const failed = marker === "failed";
  const returnUrl = failed ? "/" : returnUrlWithoutMarker();
  const auto = marker === null && session.isFetchedAfterMount && session.data?.deviceSignedIn === true;

  useEffect(() => {
    if (auto) window.location.assign(signInHref(returnUrl, { auto: true }));
  }, [auto, returnUrl]);

  if (marker === null && !session.isError && (!session.isFetchedAfterMount || auto)) {
    return (
      <main className="grid min-h-dvh place-items-center bg-canvas" aria-busy="true">
        {auto && <p className="text-sm text-ink-2"><Trans>Signing you in…</Trans></p>}
      </main>
    );
  }

  return (
    <SignInView
      appName={appName}
      headline={headline}
      description={description}
      development={development}
      failed={failed}
      failureMessage={failureMessage}
      href={signInHref(returnUrl)}
    />
  );
}

export type SignInViewProps = Omit<SignInPageProps, "failureMessage"> & {
  /** The development sign-in is on: the button signs in the configured user, and a band says it is refused elsewhere. */
  development: boolean;
  /** The last attempt failed (`?signin=failed`): `failureMessage`, or the general one, above the button. */
  failed: boolean;
  failureMessage?: string;
  /** Where the button goes: `signInHref(returnUrl)`. */
  href: string;
  /** In a page rather than the whole window: a `div` without the full height (Core's /design). */
  inline?: boolean;
};

/** The sign-in page as people see it, apart from the session, so the specimen can show each state. */
export function SignInView({ appName, headline, description, development, failed, failureMessage, href, inline = false }: SignInViewProps) {
  const { t } = useLingui();
  const Frame = inline ? "div" : "main";
  return (
    <Frame className={cn("grid grid-cols-1 md:grid-cols-[1fr_minmax(360px,480px)]", !inline && "min-h-dvh")}>
      <section className="flex flex-col justify-between bg-canvas px-10 py-10">
        <div className="flex items-center justify-between gap-4">
          <Wordmark app={appName} />
          {/* Picked here, the language becomes the person's once they sign in. Shown only where the app offers two. */}
          <LanguageSwitch className="-me-3" />
        </div>
        <div>
          <h1 className="max-w-2xl font-display text-4xl text-ink">{headline}</h1>
          <p className="mt-5 max-w-md text-base text-ink-2">{description}</p>
        </div>
        <p className="font-mono text-xs text-ink-3">ghezaa-me.com</p> {/* i18n-ignore: the domain */}
      </section>

      <section className="flex flex-col justify-center border-t border-line bg-surface px-10 py-10 md:border-t-0 md:border-s">
        <h2 className="font-display text-xl text-ink"><Trans>Sign in</Trans></h2>
        <p className="mt-2 text-sm text-ink-2">
          {development
            ? t`Development mode is on: this signs in the configured development user without Microsoft.`
            : t`You will be sent to Microsoft and straight back, with no separate password.`}
        </p>
        {failed && <p className="mt-4 bg-danger-soft px-3 py-2 text-xs text-danger">{failureMessage ?? t`Microsoft did not complete the sign-in: try again, and tell IT if it keeps happening.`}</p>}
        <Button variant="primary" size="lg" className="mt-6 w-full" asChild>
          <a href={href}>{development ? t`Sign in as development user` : t`Continue with Microsoft`}</a>
        </Button>
        {development && (
          <p className="mt-4 bg-warning-soft px-3 py-2 text-xs text-warning">
            <Trans>Development sign-in is refused outside the Development environment.</Trans>
          </p>
        )}
      </section>
    </Frame>
  );
}
